import { Geld } from '../lib/geld';
import { feestToeslag } from '../lib/feest';
import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  parseISO,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import { Banknote, CalendarCheck, ChevronLeft, ChevronRight, Plus, PartyPopper } from 'lucide-react';
import { api } from '../lib/api';
import { cn, formatHours, formatMoney, formatTime } from '../lib/utils';
import { shiftKind, KIND_LABEL, KIND_STYLE } from '../lib/shift-kind';
import { ShiftDrawer } from '../components/ShiftDrawer';
import { fmtDuur, VerlengdTag, verlengingTekst, verlengingVan } from '../components/Verlengd';
import { CalendarWeekView } from '../components/CalendarWeekView';
import { CalendarDayView } from '../components/CalendarDayView';
import { CalendarLegend } from '../components/CalendarLegend';
import { DayPreviewTooltip } from '../components/DayPreviewTooltip';
import { PeriodBar } from '../components/PeriodBar';
import { BelangrijkeDagenLijst, DagLabel, DagRegel, DAG_META } from '../components/BelangrijkeDagen';
import { Card, ICON, IconButton, PageHeader, Skeleton, Tabs } from '../components/ui';
import type { BelangrijkeDag, CalendarPeriod } from '../lib/api';
import type { Holiday, ShiftWithCalc } from '../types';

type View = 'month' | 'week' | 'day';

const VIEWS: Array<{ value: View; label: string }> = [
  { value: 'month', label: 'Maand' },
  { value: 'week', label: 'Week' },
  { value: 'day', label: 'Dag' },
];
const NAV_LABEL: Record<View, [string, string]> = {
  month: ['Vorige maand', 'Volgende maand'],
  week: ['Vorige week', 'Volgende week'],
  day: ['Vorige dag', 'Volgende dag'],
};
const WEEKDAYS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

const capFirst = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
const ymd = (d: Date) => format(d, 'yyyy-MM-dd');
const locatieVan = (s: ShiftWithCalc) => s.object?.naam_kort ?? s.object?.naam ?? s.object_naam_snapshot ?? null;
const isAfwezig = (k: ReturnType<typeof shiftKind>) => k === 'verlof' || k === 'ziek';

/** Enter/spatie werkt als een klik (voor klikbare div's). */
function keyActivate(e: React.KeyboardEvent<HTMLElement>) {
  if (e.target !== e.currentTarget) return;
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.click();
  }
}

export function CalendarPage() {
  const qc = useQueryClient();
  const [cursor, setCursor] = useState(() => new Date());
  const [view, setView] = useState<View>('month');
  const [selected, setSelected] = useState(() => ymd(new Date()));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
  const [initialDate, setInitialDate] = useState<string | null>(null);
  const [initialStart, setInitialStart] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deeplinks: ?shift=<id> opent een dienst, ?view=week|day. Diensten komen alleen uit het rooster;
  // zelf toevoegen kan niet meer.
  useEffect(() => {
    const shift = searchParams.get('shift');
    const viewParam = searchParams.get('view') as View | null;
    if (!shift && !viewParam) return;
    const next = new URLSearchParams(searchParams);
    if (viewParam === 'week' || viewParam === 'day' || viewParam === 'month') setView(viewParam);
    next.delete('view');
    if (shift) {
      setSelectedShiftId(Number(shift));
      setDrawerOpen(true);
    }
    next.delete('new');
    next.delete('action');
    next.delete('shift');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Bereik per weergave
  const { from, to } = useMemo(() => {
    if (view === 'month') {
      return {
        from: ymd(startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 })),
        to: ymd(endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 })),
      };
    }
    if (view === 'week') {
      return {
        from: ymd(startOfWeek(cursor, { weekStartsOn: 1 })),
        to: ymd(endOfWeek(cursor, { weekStartsOn: 1 })),
      };
    }
    const d = ymd(cursor);
    return { from: d, to: d };
  }, [cursor, view]);

  const { data: shifts = [], isPending: shiftsLoading } = useQuery({
    queryKey: ['shifts', from, to],
    queryFn: () => api.shifts.list(from, to),
    placeholderData: keepPreviousData,
  });

  const { data: periods = [] } = useQuery({
    queryKey: ['periods', from, to],
    queryFn: () => api.shifts.periods(from, to),
    staleTime: 60 * 60_000,
    placeholderData: keepPreviousData,
  });
  // Periode van de getoonde datum. Maandweergave: vandaag als die in deze maand valt, anders de
  // periode met de meeste dagen in de getoonde maand (een maand past niet op één 4-weken periode,
  // en de 1e van de maand ligt vaak nog in de vorige).
  const focusPeriod = useMemo(() => {
    if (view !== 'month' || isSameMonth(new Date(), cursor)) {
      const d = ymd(view === 'month' ? new Date() : cursor);
      return periods.find((p) => p.start_date <= d && p.end_date >= d);
    }
    const ms = ymd(startOfMonth(cursor));
    const me = ymd(endOfMonth(cursor));
    const dagenInMaand = (p: CalendarPeriod) =>
      differenceInCalendarDays(parseISO(p.end_date < me ? p.end_date : me), parseISO(p.start_date > ms ? p.start_date : ms));
    return [...periods].filter((p) => p.end_date >= ms && p.start_date <= me).sort((a, b) => dagenInMaand(b) - dagenInMaand(a))[0];
  }, [periods, view, cursor]);

  const { data: holidays = [] } = useQuery({
    queryKey: ['holidays', cursor.getFullYear()],
    queryFn: () => api.cao.holidays(cursor.getFullYear()),
  });

  // Belangrijke dagen: uitkeringen, loonsverhogingen, vervallende kwalificaties/verlof, contract.
  const { data: dagen = [] } = useQuery({
    queryKey: ['belangrijke-dagen', from, to],
    queryFn: () => api.uitkeringen.dagen(from, to),
    staleTime: 30 * 60_000,
  });
  // Ziek gemeld op een dag zonder dienst (bv. 07-10-2026): wel ziek volgens AFAS, geen ziekteloon.
  const { data: ziekte = [] } = useQuery({
    queryKey: ['ziekte-periodes'],
    queryFn: api.leave.ziektePeriodes.list,
    staleTime: 30 * 60_000,
  });
  const alleDagen = useMemo(() => {
    const metDienst = new Set(shifts.map((s) => s.shift_date));
    const extra: BelangrijkeDag[] = [];
    for (const z of ziekte) {
      const eind = z.end_date ?? ymd(new Date());
      for (let d = parseISO(z.start_date); ymd(d) <= eind; d = addDays(d, 1)) {
        const ds = ymd(d);
        if (ds >= from && ds <= to && !metDienst.has(ds)) {
          extra.push({ date: ds, kind: 'ziek', title: 'Ziek gemeld', detail: 'Geen dienst gepland, dus geen ziekteloon voor deze dag.' });
        }
      }
    }
    return [...dagen, ...extra];
  }, [dagen, ziekte, shifts, from, to]);
  const dagenByDate = useMemo(() => {
    const m = new Map<string, BelangrijkeDag[]>();
    for (const d of alleDagen) m.set(d.date, [...(m.get(d.date) ?? []), d]);
    return m;
  }, [alleDagen]);
  const maandDagen = useMemo(() => {
    const ms = ymd(startOfMonth(cursor));
    const me = ymd(endOfMonth(cursor));
    return dagen.filter((d) => d.date >= ms && d.date <= me);
  }, [dagen, cursor]);

  const holidayMap = useMemo<Map<string, Holiday>>(() => new Map(holidays.map((h) => [h.holiday_date, h])), [holidays]);

  const updateMut = useMutation({
    mutationFn: (v: { id: number; data: any }) => api.shifts.update(v.id, v.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shifts'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['compare'] });
    },
  });

  function handleShiftMove(shiftId: number, newDate: string) {
    updateMut.mutate({ id: shiftId, data: { shift_date: newDate } });
  }

  function handleShiftResize(shiftId: number, newEndTime: string, newDate?: string) {
    const shift = shifts.find((s) => s.id === shiftId);
    if (!shift) return;
    // Resize naar dezelfde dag waarop de dienst start, maar met een eindtijd <= starttijd,
    // zou de backend onbedoeld als nachtdienst (crosses_midnight) berekenen. Negeer zo'n
    // ongeldige resize i.p.v. de ORT te verzieken; de gebruiker corrigeert via het paneel.
    const droppedOnStartDay = !newDate || newDate === shift.shift_date;
    if (droppedOnStartDay && newEndTime <= shift.start_time) return;
    updateMut.mutate({ id: shiftId, data: { end_time: newEndTime } });
  }

  function handleShiftClick(id: number) {
    setSelectedShiftId(id);
    setInitialStart(null);
    setDrawerOpen(true);
  }

  function shiftNav(direction: -1 | 1) {
    // Altijd naar de 1e: addMonths behoudt de dag van de maand (30-10 → 30-11) en klemt aan het
    // maandeinde (31-01 → 28-02 → 28-03), waardoor de cursor op een toevallige dag belandt.
    if (view === 'month') setCursor(startOfMonth(addMonths(cursor, direction)));
    else if (view === 'week') setCursor(addWeeks(cursor, direction));
    else setCursor(addDays(cursor, direction));
  }

  function goToday() {
    setCursor(new Date());
    setSelected(ymd(new Date()));
  }

  const title = useMemo(() => {
    if (view === 'month') return capFirst(format(cursor, 'MMMM yyyy', { locale: nl }));
    if (view === 'week') {
      const ws = startOfWeek(cursor, { weekStartsOn: 1 });
      const we = endOfWeek(cursor, { weekStartsOn: 1 });
      return `Week ${format(ws, 'I')}, ${format(ws, 'd MMM', { locale: nl })} – ${format(we, 'd MMM', { locale: nl })}`;
    }
    return capFirst(format(cursor, 'EEEE d MMMM yyyy', { locale: nl }));
  }, [cursor, view]);

  const [prevLabel, nextLabel] = NAV_LABEL[view];

  return (
    <div className="page">
      <PageHeader title={title} />

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex items-center gap-1">
          <IconButton icon={ChevronLeft} label={prevLabel} onClick={() => shiftNav(-1)} />
          <IconButton icon={ChevronRight} label={nextLabel} onClick={() => shiftNav(1)} />
          <button type="button" onClick={goToday} className="btn-secondary ml-1">
            Vandaag
          </button>
        </div>
        <Tabs items={VIEWS} value={view} onChange={setView} />
      </div>

      <PeriodBar period={focusPeriod} />

      {shiftsLoading ? (
        <Skeleton className="h-[28rem] w-full rounded-xl" />
      ) : (
        <>
          {view === 'month' && (
            <MonthView
              cursor={cursor}
              from={from}
              to={to}
              periods={periods}
              shifts={shifts}
              holidayMap={holidayMap}
              dagenByDate={dagenByDate}
              selected={selected}
              onSelect={setSelected}
              onCellClick={(d) => setSelected(d)}
              onShiftClick={handleShiftClick}
              onShiftMove={handleShiftMove}
              onDayZoom={(d) => {
                setCursor(parseISO(d));
                setView('day');
              }}
            />
          )}
          {view === 'week' && (
            <CalendarWeekView
              cursor={cursor}
              shifts={shifts}
              holidayMap={holidayMap}
              onShiftClick={handleShiftClick}
              onEmptySlotClick={() => {}}
              onShiftMove={handleShiftMove}
              onShiftResize={handleShiftResize}
            />
          )}
          {view === 'day' && (
            <CalendarDayView
              cursor={cursor}
              shifts={shifts}
              holiday={holidayMap.get(ymd(cursor)) ?? null}
              onShiftClick={handleShiftClick}
              onEmptySlotClick={() => {}}
            />
          )}
        </>
      )}

      {view === 'month' && <BelangrijkeDagenLijst dagen={maandDagen} titel="Belangrijke dagen deze maand" />}

      <CalendarLegend />

      <ShiftDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedShiftId(null);
          setInitialDate(null);
          setInitialStart(null);
        }}
        shiftId={selectedShiftId}
        initialDate={initialDate}
        initialStartTime={initialStart}
      />
    </div>
  );
}

/* --------------------- Maandweergave --------------------- */

type MonthProps = {
  cursor: Date;
  from: string;
  to: string;
  periods: CalendarPeriod[];
  shifts: ShiftWithCalc[];
  holidayMap: Map<string, Holiday>;
  dagenByDate: Map<string, BelangrijkeDag[]>;
  selected: string;
  onSelect: (date: string) => void;
  onCellClick: (date: string) => void;
  onShiftClick: (id: number) => void;
  onShiftMove: (id: number, date: string) => void;
  onDayZoom: (date: string) => void;
};

type CellCtx = MonthProps & {
  periodStart: Map<string, CalendarPeriod>;
  paydays: Map<string, number>;
  shiftsByDate: Map<string, ShiftWithCalc[]>;
};

function MonthView(props: MonthProps) {
  const { cursor, periods, shifts, from, to } = props;
  const weeks = useMemo(() => {
    const days = eachDayOfInterval({ start: parseISO(from), end: parseISO(to) });
    const out: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) out.push(days.slice(i, i + 7));
    return out;
  }, [from, to]);

  // Periode-start en betaaldagen.
  const periodStart = useMemo(() => new Map(periods.map((p) => [p.start_date, p])), [periods]);
  const paydays = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of periods) {
      // payday_in_period = uitbetaling van de vórige periode
      if (p.payday_in_period) m.set(p.payday_in_period, p.period_number === 1 ? 13 : p.period_number - 1);
      if (p.payday && !m.has(p.payday)) m.set(p.payday, p.period_number);
    }
    return m;
  }, [periods]);

  const shiftsByDate = useMemo(() => {
    const map = new Map<string, ShiftWithCalc[]>();
    for (const s of shifts) {
      if (!map.has(s.shift_date)) map.set(s.shift_date, []);
      map.get(s.shift_date)!.push(s);
    }
    for (const list of map.values()) list.sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [shifts]);

  // Geselecteerde dag (mobiel): de gekozen dag als die in beeld is, anders vandaag of de 1e van de maand.
  const today = ymd(new Date());
  const sel =
    props.selected >= from && props.selected <= to
      ? props.selected
      : isSameMonth(new Date(), cursor)
        ? today
        : ymd(startOfMonth(cursor));

  const ctx: CellCtx = { ...props, selected: sel, periodStart, paydays, shiftsByDate };

  return (
    <>
      {/* Desktop: volledig rooster met weekkolom */}
      <div className="card-flush enter hidden overflow-visible lg:block">
        <div className="grid grid-cols-[repeat(7,minmax(0,1fr))_6.5rem] border-b border-surface-line">
          {WEEKDAYS.map((d) => (
            <div key={d} className="t-label px-3 py-2.5">
              {d}
            </div>
          ))}
          <div className="t-label border-l border-surface-line px-3 py-2.5 text-right">Week</div>
        </div>
        {weeks.map((week, wi) => (
          <div
            key={week[0].toISOString()}
            className="grid grid-cols-[repeat(7,minmax(0,1fr))_6.5rem] border-b border-surface-line/60 last:border-b-0"
          >
            {week.map((day, di) => (
              <DayCell key={day.toISOString()} day={day} ctx={ctx} placement={wi >= weeks.length - 2 ? 'top' : 'bottom'} align={di >= 4 ? 'right' : 'left'} />
            ))}
            <WeekTotal week={week} shiftsByDate={shiftsByDate} />
          </div>
        ))}
      </div>

      {/* Mobiel: compact rooster met stippen, daaronder de week van de gekozen dag */}
      <div className="space-y-4 lg:hidden">
        <div className="card-flush enter">
          <div className="grid grid-cols-7 border-b border-surface-line">
            {WEEKDAYS.map((d) => (
              <div key={d} className="t-label py-2 text-center">
                {d}
              </div>
            ))}
          </div>
          {weeks.map((week) => (
            <div key={week[0].toISOString()} className="grid grid-cols-7">
              {week.map((day) => (
                <MobileDayCell key={day.toISOString()} day={day} ctx={ctx} />
              ))}
            </div>
          ))}
        </div>
        <MobileWeekAgenda ctx={ctx} />
      </div>
    </>
  );
}

let dragId: number | null = null;

function DayCell({ day, ctx, placement, align }: { day: Date; ctx: CellCtx; placement: 'top' | 'bottom'; align: 'left' | 'right' }) {
  const dateStr = ymd(day);
  const inMonth = isSameMonth(day, ctx.cursor);
  const isToday = dateStr === ymd(new Date());
  const holiday = ctx.holidayMap.get(dateStr);
  const dayShifts = ctx.shiftsByDate.get(dateStr) ?? [];
  const pStart = ctx.periodStart.get(dateStr);
  const payFor = ctx.paydays.get(dateStr);
  const belangrijk = ctx.dagenByDate.get(dateStr) ?? [];

  return (
    <DayPreviewTooltip date={dateStr} shifts={dayShifts} placement={placement} align={align}>
      <div
        role="button"
        tabIndex={0}
        aria-label={format(day, 'EEEE d MMMM', { locale: nl })}
        onClick={() => ctx.onCellClick(dateStr)}
        onKeyDown={keyActivate}
        onDoubleClick={() => ctx.onDayZoom(dateStr)}
        onDragOver={(e) => {
          if (dragId != null) e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (dragId != null) {
            ctx.onShiftMove(dragId, dateStr);
            dragId = null;
          }
        }}
        className={cn(
          'group relative h-full min-h-[7.5rem] min-w-0 cursor-pointer border-r border-surface-line/60 p-2 transition-colors duration-fast ease-calm hover:bg-surface-raised/60',
          pStart && 'shadow-[inset_2px_0_0_0_theme(colors.brand.400)]'
        )}
      >
        <div className="mb-1.5 flex min-w-0 items-center justify-between gap-1">
          <span
            className={cn(
              't-num inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm',
              isToday ? 'bg-brand-400 font-semibold text-slate-950' : inMonth ? 'text-slate-200' : 'text-slate-600',
              holiday && !isToday && 'text-rose-300'
            )}
          >
            {format(day, 'd')}
          </span>
          <span className="flex min-w-0 items-center gap-1.5 text-xs">
            {pStart && (
              <span className="shrink-0 font-medium text-brand-300" title={`Start loonperiode ${pStart.period_number}`}>
                P{pStart.period_number}
              </span>
            )}
            {payFor != null && (
              <span className="inline-flex shrink-0 items-center gap-1 text-money" title={`Uitbetaling periode ${payFor}`}>
                <Banknote className={ICON.sm} aria-hidden /> P{payFor}
              </span>
            )}
            {holiday && !payFor && (
              <span className="truncate text-rose-300" title={`${holiday.name}: ${feestToeslag(dateStr).lang}`}>
                {holiday.name} <span className="t-num font-medium">{feestToeslag(dateStr).kort}</span>
              </span>
            )}
          </span>
        </div>

        {belangrijk.length > 0 && (
          <div className="mb-1 space-y-0.5">
            {belangrijk.map((b, i) => (
              <DagLabel key={i} d={b} />
            ))}
          </div>
        )}

        <div className="space-y-1">
          {dayShifts.slice(0, 3).map((s) => (
            <ShiftChip key={s.id} s={s} onClick={() => ctx.onShiftClick(s.id)} dim={!inMonth} />
          ))}
          {dayShifts.length > 3 && <div className="px-1.5 text-xs text-slate-500">+{dayShifts.length - 3} meer</div>}
        </div>

      </div>
    </DayPreviewTooltip>
  );
}

function ShiftChip({ s, onClick, dim }: { s: ShiftWithCalc; onClick: () => void; dim: boolean }) {
  const kind = shiftKind(s);
  const st = KIND_STYLE[kind];
  const locatie = locatieVan(s);
  const afwezig = isAfwezig(kind);
  const verlengd = afwezig ? null : verlengingVan(s);
  return (
    <div
      draggable
      role="button"
      tabIndex={0}
      onDragStart={(e) => {
        dragId = s.id;
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={() => (dragId = null)}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onKeyDown={keyActivate}
      title={`${KIND_LABEL[kind]} ${formatTime(s.start_time)}–${formatTime(s.end_time)}${verlengd ? ` (verlengd +${fmtDuur(verlengd.minuten)}: ${verlengingTekst(verlengd)})` : ''}${locatie ? `, ${locatie}` : ''}`}
      className={cn(
        'min-w-0 rounded-md border-l-2 px-1.5 py-1 transition-[filter] duration-fast ease-calm hover:brightness-125',
        st.bar,
        st.bg,
        dim && 'opacity-50'
      )}
    >
      <div className="flex min-w-0 items-baseline gap-1">
        <span className={cn('t-num truncate text-xs font-semibold leading-tight', st.text)}>
          {afwezig ? KIND_LABEL[kind] : `${formatTime(s.start_time)}–${formatTime(s.end_time)}`}
        </span>
        {!afwezig && <VerlengdTag shift={s} className="shrink-0 leading-tight" />}
      </div>
      <div className="mt-0.5 flex min-w-0 items-baseline justify-between gap-1 text-xs leading-tight text-slate-400">
        <span className="truncate">{afwezig ? '' : locatie ?? ''}</span>
        {s.total_gross > 0 && <span className="t-num shrink-0"><Geld bruto={s.total_gross} date={s.shift_date} /></span>}
      </div>
    </div>
  );
}

function weekSom(week: Date[], shiftsByDate: Map<string, ShiftWithCalc[]>) {
  let hours = 0;
  let gross = 0;
  for (const d of week) {
    for (const s of shiftsByDate.get(ymd(d)) ?? []) {
      hours += s.total_hours ?? 0;
      gross += s.total_gross ?? 0;
    }
  }
  return { hours, gross };
}

/** Weektotaal in een eigen kolom (desktop): uren en verdiensten (bruto of netto) van ma t/m zo. */
function WeekTotal({ week, shiftsByDate }: { week: Date[]; shiftsByDate: Map<string, ShiftWithCalc[]> }) {
  const { hours, gross } = weekSom(week, shiftsByDate);
  return (
    <div className="flex flex-col items-end gap-0.5 border-l border-surface-line px-3 py-2.5 text-right">
      <span className="t-label">Week {format(week[0], 'I')}</span>
      {hours > 0 ? (
        <>
          <span className="t-num text-sm font-semibold text-slate-100">{formatHours(hours)}</span>
          <span className="t-num text-xs text-slate-400"><Geld bruto={gross} date={format(week[0], 'yyyy-MM-dd')} /></span>
        </>
      ) : (
        <span className="text-sm text-slate-600">–</span>
      )}
    </div>
  );
}

/* --------------------- Mobiel --------------------- */

/** Dagcel op mobiel: dagnummer, een stip per dienst (kleur = soort), een markering voor betaal- of belangrijke dag. */
function MobileDayCell({ day, ctx }: { day: Date; ctx: CellCtx }) {
  const dateStr = ymd(day);
  const inMonth = isSameMonth(day, ctx.cursor);
  const isToday = dateStr === ymd(new Date());
  const isSel = dateStr === ctx.selected;
  const holiday = ctx.holidayMap.get(dateStr);
  const dayShifts = ctx.shiftsByDate.get(dateStr) ?? [];
  const pStart = ctx.periodStart.get(dateStr);
  const payFor = ctx.paydays.get(dateStr);
  const belangrijk = ctx.dagenByDate.get(dateStr) ?? [];
  const marker = payFor != null ? 'bg-money' : belangrijk.length ? DAG_META[belangrijk[0].kind].dot : null;

  return (
    <button
      type="button"
      onClick={() => ctx.onSelect(dateStr)}
      aria-pressed={isSel}
      aria-label={`${format(day, 'EEEE d MMMM', { locale: nl })}${dayShifts.length ? `, ${dayShifts.length} ${dayShifts.length === 1 ? 'dienst' : 'diensten'}` : ''}${holiday ? `, ${holiday.name}` : ''}`}
      className={cn(
        'relative flex min-h-[52px] min-w-0 flex-col items-center gap-1 pb-1.5 pt-1 transition-colors duration-fast ease-calm active:bg-surface-raised',
        isSel && 'bg-surface-raised',
        pStart && 'shadow-[inset_2px_0_0_0_theme(colors.brand.400)]'
      )}
    >
      <span
        className={cn(
          't-num inline-flex h-7 w-7 items-center justify-center rounded-full text-sm',
          isSel && isToday
            ? 'bg-brand-400 font-semibold text-slate-950'
            : isSel
              ? 'bg-slate-100 font-semibold text-slate-950'
              : isToday
                ? 'font-semibold text-brand-300'
                : holiday
                  ? 'text-rose-300'
                  : inMonth
                    ? 'text-slate-200'
                    : 'text-slate-600'
        )}
      >
        {format(day, 'd')}
      </span>
      <span className={cn('flex h-1.5 items-center gap-0.5', !inMonth && 'opacity-50')} aria-hidden>
        {dayShifts.slice(0, 3).map((s) => (
          <span key={s.id} className={cn('h-1.5 w-1.5 rounded-full', KIND_STYLE[shiftKind(s)].dot)} />
        ))}
      </span>
      {marker && <span className={cn('absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full', marker)} aria-hidden />}
    </button>
  );
}

/** Onder het mobiele rooster: alleen de week van de gekozen dag, met weektotaal. */
function MobileWeekAgenda({ ctx }: { ctx: CellCtx }) {
  const selDate = parseISO(ctx.selected);
  const week = eachDayOfInterval({ start: startOfWeek(selDate, { weekStartsOn: 1 }), end: endOfWeek(selDate, { weekStartsOn: 1 }) });
  const { hours, gross } = weekSom(week, ctx.shiftsByDate);
  const today = ymd(new Date());
  const days = week
    .map(ymd)
    .filter((d) => d === ctx.selected || ctx.shiftsByDate.has(d) || ctx.paydays.has(d) || ctx.holidayMap.has(d) || ctx.dagenByDate.has(d));

  return (
    <Card
      flush
      title={`Week ${format(selDate, 'I')}`}
      action={
        <div className="flex items-center gap-2">
          {hours > 0 && (
            <span className="t-num text-sm text-slate-400">
              {formatHours(hours)}, <Geld bruto={gross} date={ctx.selected} />
            </span>
          )}
        </div>
      }
    >
      <div className="list border-t border-surface-line">
        {days.map((d) => {
          const date = parseISO(d);
          const list = ctx.shiftsByDate.get(d) ?? [];
          const isSel = d === ctx.selected;
          const extra = [
            ctx.paydays.has(d) && (
              <div key="pay" className="flex items-center gap-2 text-sm text-money">
                <Banknote className={cn(ICON.sm, 'shrink-0')} aria-hidden /> Uitbetaling periode {ctx.paydays.get(d)}
              </div>
            ),
            ctx.holidayMap.has(d) && (
              <div key="hol" className="flex items-center gap-2 text-sm text-rose-300">
                <PartyPopper className={cn(ICON.sm, 'shrink-0')} aria-hidden /> {ctx.holidayMap.get(d)!.name}
                <span className="t-num text-xs text-rose-200/80">{feestToeslag(d).lang}</span>
              </div>
            ),
            ...(ctx.dagenByDate.get(d) ?? []).map((b, i) => <DagRegel key={`b${i}`} d={b} />),
          ].filter(Boolean);
          return (
            <div key={d} className={cn('flex gap-3 px-4 py-2.5', isSel && 'bg-surface-raised/50', d < today && !isSel && 'opacity-70')}>
              <button type="button" onClick={() => ctx.onSelect(d)} className="w-10 shrink-0 pt-0.5 text-left">
                <div className="t-label">{format(date, 'EEE', { locale: nl })}</div>
                <div className={cn('t-num text-sm font-semibold', d === today ? 'text-brand-300' : 'text-slate-100')}>{format(date, 'd')}</div>
              </button>
              <div className="min-w-0 flex-1 space-y-1.5">
                {extra}
                {list.map((s) => {
                  const kind = shiftKind(s);
                  const locatie = locatieVan(s);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => ctx.onShiftClick(s.id)}
                      className="-mx-2 flex min-h-[44px] w-[calc(100%+1rem)] items-center gap-3 rounded-lg px-2 py-1 text-left transition-colors duration-fast ease-calm hover:bg-surface-raised active:bg-slate-700/40"
                    >
                      <span className={cn('h-2 w-2 shrink-0 rounded-full', KIND_STYLE[kind].dot)} aria-hidden />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline gap-2">
                          <span className="t-num text-sm font-medium text-slate-100">
                            {formatTime(s.start_time)}–{formatTime(s.end_time)}
                          </span>
                          <VerlengdTag shift={s} />
                          <span className="text-xs text-slate-400">{KIND_LABEL[kind]}</span>
                        </span>
                        {locatie && !isAfwezig(kind) && <span className="block truncate text-xs text-slate-400">{locatie}</span>}
                      </span>
                      {s.total_gross > 0 && <span className="t-num shrink-0 text-sm text-slate-200"><Geld bruto={s.total_gross} date={s.shift_date} /></span>}
                      <ChevronRight className={cn(ICON.sm, 'shrink-0 text-slate-500')} aria-hidden />
                    </button>
                  );
                })}
                {list.length === 0 && extra.length === 0 && (
                  <div className="flex min-h-[44px] items-center gap-2 text-sm text-slate-500">
                    <CalendarCheck className={cn(ICON.sm, 'shrink-0')} aria-hidden /> Vrij
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
